import { useEffect, useRef, type RefObject } from 'react'
import { useLocation } from 'react-router'

/** How long to wait for a page that renders its `<h1>` only after loading data. */
const WAIT_MS = 3000

function focusHeading(heading: HTMLElement): void {
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
  heading.focus({ preventScroll: true })
}

/**
 * After a route change (not the first render) moves the focus to the new page's
 * `<h1>` inside `main`, so a keyboard or screen-reader user starts at the top of
 * the new page. A search-param change (a tab switch) leaves the focus alone. When
 * the heading is not there yet (the page loads first) it waits for it, briefly.
 */
export function useRouteFocus(mainRef: RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation()
  const previous = useRef(pathname)

  useEffect(() => {
    if (previous.current === pathname) return
    previous.current = pathname
    const main = mainRef.current
    if (!main) return

    const heading = main.querySelector<HTMLElement>('h1')
    if (heading) {
      focusHeading(heading)
      return
    }
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(() => {
      const found = main.querySelector<HTMLElement>('h1')
      if (!found) return
      observer.disconnect()
      window.clearTimeout(timer)
      // Someone already started typing somewhere else: do not take the focus away.
      const active = document.activeElement
      if (active && active !== document.body && main.contains(active) && active !== main) return
      focusHeading(found)
    })
    const timer = window.setTimeout(() => observer.disconnect(), WAIT_MS)
    observer.observe(main, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      window.clearTimeout(timer)
    }
  }, [pathname, mainRef])
}
